import { useCallback } from 'react';
import { Button } from 'tuui';
import { Radio } from 'taboola-ultimate-ui';
import { RadioGroupField } from 'modules/campaigns/components/Form';
import { GTM_EVENTS } from 'modules/taboola-common-frontend-modules/gtmTracker';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import { CONVERSION_TYPE } from 'modules/tracking/config';
import { conversionTypeOptions } from '../../config/conversionTypeOptions';
import { STEP_IDS } from '../../config/stepIds';
import { NewEventHeader } from '../Headers/NewEventHeader';
import styles from './conversionTypeSetup.module.scss';

export const ConversionTypeSetup = ({ navigateToStepId, currentConversion, handleCancelWizard, updateDraftEvent }) => {
    const handleTypeChange = useCallback(
        newType => {
            if (currentConversion) {
                updateDraftEvent({ ...currentConversion, type: newType });
            }
        },
        [currentConversion, updateDraftEvent]
    );

    const selectedValue = currentConversion?.type ?? CONVERSION_TYPE.EVENT_BASED;

    const determineNextStepId = useCallback(() => {
        if (!currentConversion?.type) {
            return null;
        }

        if (currentConversion.type === CONVERSION_TYPE.EVENT_BASED) {
            return STEP_IDS.BUTTON_CONVERSION_LOCATION_SELECTOR;
        }

        return STEP_IDS.SETUP_URL_EVENT;
    }, [currentConversion?.type]);

    const handleNext = () => {
        const nextStepId = determineNextStepId();
        if (nextStepId && navigateToStepId) {
            navigateToStepId(nextStepId);
        }
    };

    return (
        <div className={styles['container']}>
            <NewEventHeader handlerArrowLeftButton={handleCancelWizard} />
            <RadioGroupField
                groupTitle={
                    <FormattedMessage id="codeless.conversion.type.header" defaultMessage="Select a conversion type" />
                }
                radioGroupName="conversionType"
                onChange={handleTypeChange}
                selectedValue={selectedValue}
                data-metrics-component="conversion-type"
                className={styles['group']}
            >
                {conversionTypeOptions.map(({ value: optionValue, title, description }) => (
                    <Radio
                        labelClassName={styles['radio']}
                        descriptionClassName={styles['description']}
                        aria-label={`${optionValue}`}
                        key={`conversion-type-${optionValue}`}
                        value={optionValue}
                        title={title}
                        description={description}
                        data-metrics-event-name={GTM_EVENTS.USABILITY}
                        data-metrics-value={optionValue}
                        inputClassName={styles['input']}
                    />
                ))}
            </RadioGroupField>
            <footer className={styles['footer']}>
                <Button
                    onClick={handleCancelWizard}
                    variant={Button.variant.outline}
                    size={Button.size.md}
                    aria-label="Cancel"
                >
                    <FormattedMessage id="codeless.cancel.button" defaultMessage="Cancel" />
                </Button>
                <Button
                    onClick={handleNext}
                    variant={Button.variant.primary}
                    size={Button.size.md}
                    className={styles['next-button']}
                    aria-label="Next"
                    disabled={!currentConversion?.id || !currentConversion?.type}
                >
                    <FormattedMessage id="codeless.next.button" defaultMessage="Next" />
                </Button>
            </footer>
        </div>
    );
};
